import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bot,
  Sparkles,
  X,
  Send,
  Loader2,
  TrendingDown,
  TrendingUp,
  Info,
  Clock3,
  ExternalLink,
  ChevronRight,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHART_COLORS = ["#63AC9A", "#3b82f6", "#f59e0b", "#8b5cf6"];

const DEFAULT_SUGGESTIONS = [
  "¿Qué productos bajaron sus ventas frente al período anterior?",
  "¿Cuáles son los clientes que más compraron en los últimos 30 días?",
  "¿Qué asesor generó más ventas reales este mes?",
  "¿Cuál es la conversión de cotizaciones a órdenes de compra?",
];

function formatGeneratedAt(value) {
  if (!value) return "Sin actualización";
  try {
    return new Date(value).toLocaleString("es-EC", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Actualizado recientemente";
  }
}

function InsightIcon({ severity }) {
  if (severity === "positive") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (severity === "warning") return <TrendingDown className="w-4 h-4 text-amber-600" />;
  return <Info className="w-4 h-4 text-blue-600" />;
}

function AnalystChart({ chart }) {
  if (!chart?.data?.length || !chart?.series?.length) return null;

  const tooltipStyle = { borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12 };
  const common = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey={chart.x_key} fontSize={10} stroke="#9ca3af" tick={{ fill: "#6b7280" }} />
      <YAxis fontSize={10} stroke="#9ca3af" tick={{ fill: "#6b7280" }} />
      <Tooltip contentStyle={tooltipStyle} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
    </>
  );

  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-4 h-4 text-[#63AC9A]" />
        <p className="text-xs font-semibold text-gray-700">{chart.title || "Análisis"}</p>
      </div>
      <div className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === "line" ? (
            <LineChart data={chart.data}>
              {common}
              {chart.series.map((series, index) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.name}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          ) : chart.type === "pie" ? (
            <PieChart>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Pie
                data={chart.data}
                dataKey={chart.series[0]?.key || "value"}
                nameKey={chart.x_key}
                innerRadius={48}
                outerRadius={78}
                paddingAngle={2}
              >
                {chart.data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          ) : (
            <BarChart data={chart.data}>
              {common}
              {chart.series.map((series, index) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.name}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CommercialAnalyst() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Soy el Analista Inteligente de Gimmicks. Puedo analizar ventas reales, productos, clientes, asesores, cotizaciones y órdenes de compra usando los datos actuales del CRM.",
      followUps: DEFAULT_SUGGESTIONS.slice(0, 3),
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchInsights = useCallback(async () => {
    try {
      setLoadingInsights(true);
      const response = await axios.get(`${API_URL}/api/ai-analytics/insights`);
      setInsights(response.data?.insights || []);
      setGeneratedAt(response.data?.generated_at || null);
    } catch {
      setInsights([]);
    } finally {
      setLoadingInsights(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
    const interval = window.setInterval(fetchInsights, 60 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [fetchInsights]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, open]);

  const historyForApi = useMemo(
    () => messages.slice(-8).map((message) => ({ role: message.role, content: message.content })),
    [messages]
  );

  const askQuestion = useCallback(async (question) => {
    const clean = (question || "").trim();
    if (!clean || sending) return;

    const userMessage = { role: "user", content: clean };
    setMessages((current) => [...current, userMessage]);
    setInput("");
    setSending(true);

    try {
      const response = await axios.post(`${API_URL}/api/n8n-agents/commercial/chat`, {
        message: clean,
        history: historyForApi,
      });
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: response.data?.answer || "No pude completar el análisis con los datos disponibles.",
          chart: response.data?.chart || null,
          references: response.data?.references || [],
          followUps: response.data?.follow_ups || [],
          period: response.data?.period || null,
        },
      ]);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: detail || "No pude consultar las métricas en este momento. Intenta nuevamente.",
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [historyForApi, sending]);

  const openInsight = (insight) => {
    setOpen(true);
    setInput(insight.question || `Analiza este hallazgo: ${insight.title}`);
  };

  return (
    <>
      <Card className="bg-gradient-to-br from-white to-[#63AC9A]/5 border-[#63AC9A]/20 shadow-sm" data-testid="commercial-ai-radar">
        <CardHeader className="pb-3 border-b border-[#63AC9A]/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base text-gray-800">
                <span className="w-8 h-8 rounded-xl bg-[#63AC9A]/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#4F9A87]" />
                </span>
                Radar Comercial IA
              </CardTitle>
              <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-400">
                <Clock3 className="w-3 h-3" />
                Análisis automático cada hora · {formatGeneratedAt(generatedAt)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={fetchInsights} className="gap-1.5 text-gray-500" disabled={loadingInsights}>
                <RefreshCw className={`w-3.5 h-3.5 ${loadingInsights ? "animate-spin" : ""}`} />
                Actualizar
              </Button>
              <Button size="sm" onClick={() => setOpen(true)} className="gap-2 bg-[#63AC9A] hover:bg-[#559c8b] text-white">
                <Bot className="w-4 h-4" />
                Preguntar a la IA
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loadingInsights ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Analizando el CRM…
            </div>
          ) : insights.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {insights.slice(0, 3).map((insight, index) => (
                <button
                  type="button"
                  key={`${insight.kind || "insight"}-${index}`}
                  onClick={() => openInsight(insight)}
                  className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-[#63AC9A]/50 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center ${insight.severity === "warning" ? "bg-amber-50" : insight.severity === "positive" ? "bg-emerald-50" : "bg-blue-50"}`}>
                      <InsightIcon severity={insight.severity} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800 leading-snug">{insight.title}</p>
                      <p className="text-xs text-gray-500 mt-1.5 line-clamp-3">{insight.detail}</p>
                      {insight.recommendation && (
                        <p className="text-xs text-[#4F9A87] mt-2 font-medium">{insight.recommendation}</p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#63AC9A] transition-colors flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 p-5 text-center">
              <p className="text-sm font-medium text-gray-600">No hay alertas comerciales relevantes en este momento.</p>
              <p className="text-xs text-gray-400 mt-1">Igualmente puedes preguntarle al analista por cualquier métrica del CRM.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-4 z-40 w-14 h-14 rounded-full bg-[#1a1a1d] text-white shadow-xl hover:scale-105 transition-transform flex items-center justify-center border-2 border-[#63AC9A]"
        aria-label="Abrir Analista Inteligente"
        data-testid="commercial-ai-fab"
      >
        <Bot className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#63AC9A] border-2 border-white" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div className="absolute inset-0 bg-black/20 sm:bg-transparent pointer-events-auto" onClick={() => setOpen(false)} />
          <section
            className="pointer-events-auto absolute right-3 bottom-3 sm:right-5 sm:bottom-5 w-[calc(100vw-1.5rem)] sm:w-[440px] h-[min(720px,calc(100vh-1.5rem))] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
            data-testid="commercial-ai-chat"
          >
            <header className="bg-[#1a1a1d] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-[#63AC9A] flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Analista Inteligente</p>
                <p className="text-[11px] text-gray-400">Conectado a las métricas del CRM</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-3 py-4 bg-[#f7f8f9] space-y-4">
              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[92%] rounded-2xl px-3.5 py-3 ${message.role === "user" ? "bg-[#63AC9A] text-white rounded-br-md" : "bg-white border border-gray-200 text-gray-700 rounded-bl-md shadow-sm"}`}>
                    <p className={`text-sm whitespace-pre-wrap leading-relaxed ${message.isError ? "text-red-600" : ""}`}>{message.content}</p>

                    {message.role === "assistant" && <AnalystChart chart={message.chart} />}

                    {message.references?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-2">Referencias del CRM</p>
                        <div className="flex flex-wrap gap-1.5">
                          {message.references.slice(0, 5).map((reference, refIndex) => (
                            <button
                              type="button"
                              key={`${reference.label}-${refIndex}`}
                              onClick={() => navigate(reference.path || reference.href || "/dashboard")}
                              className="inline-flex items-center gap-1 rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-[11px] text-gray-600 hover:border-[#63AC9A] hover:text-[#4F9A87]"
                            >
                              {reference.label}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {message.role === "assistant" && message.followUps?.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {message.followUps.slice(0, 3).map((question, followIndex) => (
                          <button
                            type="button"
                            key={`${question}-${followIndex}`}
                            onClick={() => askQuestion(question)}
                            disabled={sending}
                            className="block w-full text-left rounded-lg border border-[#63AC9A]/20 bg-[#63AC9A]/5 px-2.5 py-2 text-[11px] text-[#4F9A87] hover:bg-[#63AC9A]/10"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin text-[#63AC9A]" /> Analizando datos del CRM…
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                askQuestion(input);
              }}
              className="p-3 bg-white border-t border-gray-200 flex items-end gap-2 flex-shrink-0"
            >
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    askQuestion(input);
                  }
                }}
                rows={1}
                placeholder="Pregunta sobre ventas, productos, clientes…"
                className="flex-1 min-h-[42px] max-h-[110px] resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#63AC9A] focus:ring-2 focus:ring-[#63AC9A]/10"
              />
              <Button type="submit" size="icon" disabled={!input.trim() || sending} className="h-[42px] w-[42px] rounded-xl bg-[#63AC9A] hover:bg-[#559c8b] text-white flex-shrink-0">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </section>
        </div>
      )}
    </>
  );
}
